import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PublicLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Search, MapPin, Stethoscope, Smile, Brain, Activity, Apple, Sparkles,
  Building2, Mic, HeartPulse, Dumbbell, Microscope, Leaf, ArrowRight, Star,
  ShieldCheck, Clock, CheckCircle2, Users
} from "lucide-react";

const ICONS = { stethoscope:Stethoscope, smile:Smile, brain:Brain, activity:Activity, apple:Apple, mic:Mic, "heart-pulse":HeartPulse, dumbbell:Dumbbell, sparkles:Sparkles, "building-2":Building2, microscope:Microscope, leaf:Leaf };
const CITIES = ["Santos","Praia Grande","São Vicente","Guarujá","Cubatão","Bertioga","Mongaguá","Itanhaém","Peruíbe"];

export default function Home() {
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [specialties, setSpecialties] = useState([]);
  const [pros, setPros] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/specialties").then(r=>setSpecialties(r.data)).catch(()=>{});
    api.get("/professionals",{params:{featured:true,limit:6}}).then(r=>setPros(r.data)).catch(()=>{});
    api.get("/clinics",{params:{limit:3}}).then(r=>setClinics(r.data)).catch(()=>{});
    api.get("/benefits",{params:{limit:4}}).then(r=>setBenefits(r.data)).catch(()=>{});
  }, []);

  const doSearch = (e) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (city) p.set("city", city);
    nav(`/buscar?${p.toString()}`);
  };

  return (
    <PublicLayout>
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-[#F4F8F6]">
        {/* subtle background shapes */}
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#0D7C66]/10 blur-3xl"/>
        <div className="absolute -bottom-40 right-0 w-[440px] h-[440px] rounded-full bg-[#FF7D54]/10 blur-3xl"/>

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-[1.05fr,1fr] gap-12 items-center">
          {/* copy */}
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1.5 gs-shadow" data-testid="hero-region-badge">
              <span className="relative flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-[#0D7C66] animate-ping opacity-70"/>
                <span className="relative w-2 h-2 rounded-full bg-[#0D7C66]"/>
              </span>
              <span className="text-xs font-semibold text-slate-700"><MapPin className="w-3 h-3 inline mr-1 text-[#0D7C66]"/>Baixada Santista · SP</span>
            </div>

            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-[-0.02em] leading-[0.98] text-slate-900" style={{fontFamily:"Plus Jakarta Sans"}}>
              Cuidar da<br/>
              sua saúde,<br/>
              <span className="relative inline-block">
                <span className="relative z-10 text-[#0D7C66]">perto de você.</span>
                <span aria-hidden className="absolute left-0 right-0 bottom-1 h-3 bg-[#FFD1BF] rounded-sm -z-0"/>
              </span>
            </h1>

            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Uma rede regional que conecta você aos melhores profissionais, clínicas e benefícios da Baixada Santista — com <b>carteira digital</b>, <b>agendamento online</b> e <b>descontos exclusivos</b>.
            </p>

            {/* search */}
            <form onSubmit={doSearch} className="mt-8 bg-white rounded-2xl p-2 gs-shadow-lg grid grid-cols-1 sm:grid-cols-[1fr,200px,auto] gap-1 border border-slate-100">
              <div className="flex items-center gap-2 px-4">
                <Search className="w-5 h-5 text-slate-400 shrink-0"/>
                <Input data-testid="hero-search-input" value={q} onChange={e=>setQ(e.target.value)} placeholder="Cardiologista, clareamento, fisio…" className="border-0 focus-visible:ring-0 text-slate-900 h-12 text-base"/>
              </div>
              <div className="flex items-center gap-2 px-4 sm:border-l border-slate-200">
                <MapPin className="w-5 h-5 text-slate-400 shrink-0"/>
                <select data-testid="hero-search-city" value={city} onChange={e=>setCity(e.target.value)} className="w-full h-12 bg-transparent text-slate-900 focus:outline-none text-sm">
                  <option value="">Todas as cidades</option>
                  {CITIES.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <Button data-testid="hero-search-button" type="submit" className="bg-[#0D7C66] hover:bg-[#074E41] text-white rounded-xl h-12 px-6 text-base font-semibold">
                Buscar<ArrowRight className="w-4 h-4 ml-1"/>
              </Button>
            </form>

            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <span className="text-slate-500 self-center mr-1">Populares:</span>
              {["Cardiologia","Odontologia","Psicologia","Fisioterapia","Nutrição"].map(s=>(
                <button key={s} data-testid={`hero-quick-${s}`} onClick={()=>{setQ(s); setTimeout(doSearch, 0);}} className="px-3 py-1 rounded-full bg-white border border-slate-200 hover:border-[#0D7C66] hover:text-[#0D7C66] text-slate-600">{s}</button>
              ))}
            </div>

            {/* primary CTAs */}
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-cta-associado" size="lg" onClick={()=>nav("/cadastro?role=associate")} className="bg-[#FF7D54] hover:bg-[#e56a44] text-white h-12 px-7 text-base font-semibold rounded-xl">
                Cadastre-se grátis<ArrowRight className="w-4 h-4 ml-1"/>
              </Button>
              <Button data-testid="hero-cta-parceiro" size="lg" variant="outline" onClick={()=>nav("/seja-parceiro")} className="h-12 px-7 text-base font-semibold rounded-xl border-slate-300 hover:border-[#0D7C66] hover:text-[#0D7C66]">
                Sou profissional
              </Button>
            </div>

            {/* trust row */}
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-600">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#0D7C66]"/>Rede validada</div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#0D7C66]"/>Cadastro em 2 min</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#0D7C66]"/>Sem burocracia</div>
            </div>
          </div>

          {/* visual */}
          <div className="relative">
            <div className="relative rounded-[36px] overflow-hidden gs-shadow-lg aspect-[4/5] max-h-[640px]">
              <img
                src="https://images.unsplash.com/photo-1638202993928-7267aad84c31?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                alt="Profissional de saúde sorrindo"
                className="w-full h-full object-cover"
                onError={(e)=>{ e.currentTarget.src="https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=1200"; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#074E41]/60 via-transparent to-transparent"/>

              {/* floating stat card - top */}
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 gs-shadow flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#E6F4F1] flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#0D7C66]"/>
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-medium">Rede regional</div>
                  <div className="font-extrabold text-slate-900">+180 profissionais</div>
                </div>
              </div>

              {/* floating rating - bottom left */}
              <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 gs-shadow">
                <div className="flex items-center gap-1 mb-0.5">
                  {[1,2,3,4,5].map(i=><Star key={i} className="w-3.5 h-3.5 fill-[#FF7D54] text-[#FF7D54]"/>)}
                </div>
                <div className="text-xs text-slate-600"><b className="text-slate-900">4,9/5</b> em avaliações reais</div>
              </div>
            </div>

            {/* discount pill floating out */}
            <div className="hidden sm:block absolute -bottom-6 -right-4 bg-[#0D7C66] text-white rounded-2xl px-5 py-4 gs-shadow-lg rotate-2">
              <div className="text-[10px] uppercase tracking-widest opacity-80">Assine e economize</div>
              <div className="text-2xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>até 40% off</div>
              <div className="text-[10px] opacity-80">em consultas e exames</div>
            </div>
          </div>
        </div>

        {/* trust bar */}
        <div className="relative border-t border-slate-200 bg-white/60 backdrop-blur">
          <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              {n:"+180", l:"Profissionais"},
              {n:"+120", l:"Benefícios ativos"},
              {n:"9", l:"Cidades atendidas"},
              {n:"100%", l:"Regional • Baixada"},
            ].map((k)=>(
              <div key={k.l}>
                <div className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{fontFamily:"Plus Jakarta Sans"}}>{k.n}</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-0.5">{k.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-[#0D7C66] font-bold">Simples e direto</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2" style={{fontFamily:"Plus Jakarta Sans"}}>Do primeiro clique ao atendimento</h2>
          <p className="mt-3 text-slate-600">Uma jornada transparente que respeita seu tempo e sua privacidade.</p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            {n:"01", t:"Encontre", d:"Busque por especialidade, cidade ou nome. Filtros claros e resultados regionais."},
            {n:"02", t:"Solicite", d:"Clique em Tenho Interesse ou agende direto pelo portal — com poucos dados."},
            {n:"03", t:"Acompanhe", d:"Receba o número do pedido e siga cada etapa pelo Guia de Encaminhamento."},
          ].map((s)=>(
            <div key={s.n} className="relative rounded-3xl bg-white border border-slate-200 p-7 gs-shadow hover:-translate-y-0.5 transition">
              <div className="text-6xl font-extrabold text-[#E6F4F1] leading-none" style={{fontFamily:"Plus Jakarta Sans"}}>{s.n}</div>
              <div className="mt-3 text-xl font-bold text-slate-900">{s.t}</div>
              <div className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= SPECIALTIES ================= */}
      <section className="bg-white border-y border-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#0D7C66] font-bold">Explore por área</div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2" style={{fontFamily:"Plus Jakarta Sans"}}>Especialidades</h2>
            </div>
            <Link to="/buscar" className="text-sm text-[#0D7C66] font-semibold hover:underline" data-testid="see-all-specialties">Ver todas →</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {specialties.map(s => {
              const Icon = ICONS[s.icon] || Sparkles;
              return (
                <Link key={s.id} to={`/buscar?specialty=${encodeURIComponent(s.name)}`} data-testid={`specialty-${s.id}`}
                  className="group p-5 rounded-2xl bg-[#F4F8F6] hover:bg-[#0D7C66] transition">
                  <div className="w-11 h-11 rounded-xl bg-white text-[#0D7C66] flex items-center justify-center mb-3 group-hover:bg-white/20 group-hover:text-white transition">
                    <Icon className="w-5 h-5"/>
                  </div>
                  <div className="font-semibold text-slate-900 group-hover:text-white">{s.name}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= FEATURED PROFESSIONALS ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0D7C66] font-bold">Rede regional</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2" style={{fontFamily:"Plus Jakarta Sans"}}>Profissionais em destaque</h2>
          </div>
          <Link to="/buscar" className="text-sm text-[#0D7C66] font-semibold hover:underline" data-testid="see-all-pros">Ver todos →</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {pros.map(p => <ProCard key={p.id} p={p}/>)}
        </div>
      </section>

      {/* ================= BENEFITS TEASER ================= */}
      <section className="bg-[#FEF8F3] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div className="max-w-xl">
              <div className="text-xs uppercase tracking-widest text-[#FF7D54] font-bold">Vantagens exclusivas</div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2" style={{fontFamily:"Plus Jakarta Sans"}}>Descontos que fazem diferença</h2>
              <p className="mt-3 text-slate-600">Consultas, exames, odontologia, farmácia e bem-estar com condições especiais para associados.</p>
            </div>
            <Link to="/beneficios" className="text-sm text-[#0D7C66] font-semibold hover:underline" data-testid="see-all-benefits">Ver catálogo →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map(b=>(
              <Card key={b.id} className="overflow-hidden border-0 gs-shadow" data-testid={`benefit-card-${b.id}`}>
                {b.image_url && <img src={b.image_url} alt={b.title} className="h-32 w-full object-cover"/>}
                <div className="p-4">
                  <Badge className="bg-[#0D7C66] hover:bg-[#0D7C66] text-white mb-2">{b.category}</Badge>
                  <div className="font-semibold leading-tight text-slate-900">{b.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{b.partner_name} • {b.city}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CLINICS ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0D7C66] font-bold">Estruturas parceiras</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2" style={{fontFamily:"Plus Jakarta Sans"}}>Clínicas da rede</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {clinics.map(c=>(
            <Link key={c.id} to={`/clinicas/${c.id}`} data-testid={`clinic-card-${c.id}`} className="group rounded-3xl overflow-hidden bg-white border border-slate-200 gs-shadow hover:-translate-y-0.5 transition">
              {c.photo_url && <img src={c.photo_url} alt={c.name} className="h-44 w-full object-cover"/>}
              <div className="p-6">
                <div className="text-xs text-[#0D7C66] uppercase tracking-widest font-bold">{c.city}</div>
                <div className="font-bold text-xl mt-1 text-slate-900">{c.name}</div>
                <div className="text-sm text-slate-500 mt-2 line-clamp-2">{c.description}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ================= CLOSING CTAs ================= */}
      <section className="max-w-7xl mx-auto px-6 pb-20 grid md:grid-cols-2 gap-6">
        <div className="rounded-[32px] bg-[#0F172A] text-white p-10 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#0D7C66]/40 blur-2xl"/>
          <div className="absolute -left-10 -bottom-10 w-56 h-56 rounded-full bg-[#FF7D54]/20 blur-2xl"/>
          <div className="relative">
            <Badge className="bg-white/10 border-0 text-white mb-4">Para você e sua família</Badge>
            <h3 className="text-3xl sm:text-4xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>Seja Associado</h3>
            <p className="mt-3 text-white/80 max-w-md">A partir de <b>R$ 29,90/mês</b>. Carteira digital, benefícios em toda a rede e acompanhamento das suas solicitações.</p>
            <Button data-testid="cta-quero-associado" onClick={()=>nav("/seja-associado")} className="mt-6 bg-[#FF7D54] hover:bg-[#e56a44] h-12 px-6 rounded-xl font-semibold">
              Quero ser associado<ArrowRight className="w-4 h-4 ml-1"/>
            </Button>
          </div>
        </div>
        <div className="rounded-[32px] bg-[#E6F4F1] p-10 relative overflow-hidden">
          <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-[#0D7C66]/10 blur-2xl"/>
          <div className="relative">
            <Badge className="bg-white text-[#0D7C66] hover:bg-white mb-4">Para profissionais e clínicas</Badge>
            <h3 className="text-3xl sm:text-4xl font-extrabold text-[#074E41]" style={{fontFamily:"Plus Jakarta Sans"}}>Seja Parceiro</h3>
            <p className="mt-3 text-slate-700 max-w-md">Divulgue seu trabalho, apareça nas buscas regionais e receba oportunidades reais de contato.</p>
            <Button data-testid="cta-quero-parceiro" onClick={()=>nav("/seja-parceiro")} className="mt-6 bg-[#0D7C66] hover:bg-[#074E41] h-12 px-6 rounded-xl font-semibold">
              Quero ser parceiro<ArrowRight className="w-4 h-4 ml-1"/>
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

export function ProCard({ p }) {
  return (
    <Link to={`/profissionais/${p.id}`} data-testid={`pro-card-${p.id}`}
      className="group bg-white border border-slate-200 rounded-3xl p-5 gs-shadow hover:-translate-y-0.5 hover:border-[#0D7C66]/30 transition block">
      <div className="flex gap-4">
        {p.photo_url && <img src={p.photo_url} alt={p.name} className="w-16 h-16 rounded-2xl object-cover"/>}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-bold text-slate-900 truncate">{p.name}</div>
            {p.featured && <Badge className="bg-[#FF7D54] text-white border-0">Destaque</Badge>}
          </div>
          <div className="text-sm text-slate-500">{p.profession} • {p.specialty}</div>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3"/>{p.city}{p.neighborhood?` • ${p.neighborhood}`:""}</div>
        </div>
      </div>
      {p.benefit_summary && (
        <div className="mt-4 flex items-center gap-2 text-xs text-[#0D7C66] bg-[#E6F4F1] rounded-xl px-3 py-2">
          <Star className="w-3.5 h-3.5"/>{p.benefit_summary}
        </div>
      )}
    </Link>
  );
}
